import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Share, X } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useLive, emitSync } from "@/lib/live";
import { api } from "@/lib/api";
import { SpinningEarth } from "@/components/Earth";

export const LiveBridge = () => {
  const { user, refreshSummary } = useApp();
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) localStorage.setItem("ks_ref", ref.toUpperCase().slice(0, 20));
  }, []);
  useEffect(() => {
    const ref = localStorage.getItem("ks_ref");
    if (!user?.user_id || !ref) return;
    localStorage.removeItem("ks_ref");
    api.post("/referrals/apply", { code: ref }).then(() => toast.success("Referral code added. Your friend will thank you!")).catch(() => {});
  }, [user?.user_id]);
  useLive(user?.user_id ? { role: "user" } : null, (e) => {
    emitSync(e);
    refreshSummary();
    if (e.event === "notif") toast(e.data.title, { description: e.data.body });
  });
  return null;
};

export const PullToRefresh = ({ children }) => {
  const { refreshSummary } = useApp();
  const [dy, setDy] = useState(0);
  const [busy, setBusy] = useState(false);
  const start = useRef(null);
  const onStart = (e) => { start.current = window.scrollY <= 0 ? e.touches[0].clientY : null; };
  const onMove = (e) => { if (start.current != null) setDy(Math.max(0, Math.min(110, (e.touches[0].clientY - start.current) * 0.5))); };
  const onEnd = () => {
    if (dy > 65) { setBusy(true); emitSync({ event: "pull" }); refreshSummary(); setTimeout(() => setBusy(false), 900); }
    start.current = null; setDy(0);
  };
  const h = busy ? 56 : dy;
  return (
    <div onTouchStart={onStart} onTouchMove={onMove} onTouchEnd={onEnd} data-testid="pull-to-refresh">
      <div className="flex items-end justify-center overflow-hidden transition-[height] duration-200" style={{ height: h }}>
        {h > 10 && <div className="pb-2" style={{ transform: `rotate(${dy * 3}deg)` }}><SpinningEarth size={34} /></div>}
      </div>
      {children}
    </div>
  );
};

const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
const standalone = () => window.matchMedia("(display-mode: standalone)").matches || navigator.standalone;
const KEY = "ks_install_dismissed";

export const InstallPrompt = () => {
  const { t } = useApp();
  const [evt, setEvt] = useState(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (standalone() || Date.now() - Number(localStorage.getItem(KEY) || 0) < 3 * 86400000) return;
    const h = (e) => { e.preventDefault(); setEvt(e); };
    window.addEventListener("beforeinstallprompt", h);
    const id = setTimeout(() => setShow(true), 12000);
    return () => { window.removeEventListener("beforeinstallprompt", h); clearTimeout(id); };
  }, []);
  if (!show || (!evt && !isIos())) return null;
  const close = () => { localStorage.setItem(KEY, String(Date.now())); setShow(false); };
  const install = async () => { evt.prompt(); await evt.userChoice; close(); };
  return (
    <div className="ks-sheet fixed inset-x-0 bottom-0 z-[60] mx-auto max-w-md rounded-t-3xl bg-white p-5 shadow-[0_-8px_30px_rgba(0,0,0,.15)]" data-testid="install-prompt">
      <button onClick={close} className="absolute right-4 top-4 text-[#7B968B]" data-testid="install-close-btn" aria-label="Close"><X className="h-5 w-5" /></button>
      <div className="flex items-center gap-3">
        <img src="/icon-192.png" alt="" className="h-14 w-14 rounded-2xl" />
        <div><p className="font-display text-lg font-extrabold text-[#0D3B2B]">{t("Install Kindsaver")}</p>
          <p className="text-xs text-[#4A6358]">{t("Book pickups in one tap from your home screen. Works offline too.")}</p></div>
      </div>
      {evt ? <button onClick={install} className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-[#0D3B2B] font-bold text-white" data-testid="install-btn"><Download className="h-5 w-5" />{t("Install app")}</button>
        : <p className="mt-4 flex items-center gap-1 rounded-2xl bg-[#F4F8F5] p-3 text-sm text-[#0D3B2B]" data-testid="install-ios-steps">{t("Tap")} <Share className="h-4 w-4" /> {t("Share, then \"Add to Home Screen\".")}</p>}
    </div>
  );
};
